import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  TrendingUp, Calendar, Plus, Edit2, Trash2, Percent, Upload, Loader2,
  Sparkles, FileUp, ChevronRight, CheckCircle, Clock, AlertTriangle,
  X, Save, Building2, DollarSign, Target, Award, BarChart3, Menu, ArrowLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { authGet, authPost, authPut, authDelete } from "@/utils/auth";
import MobileBottomNav from "@/components/MobileBottomNav";
import MobileSidebar from "@/components/MobileSidebar";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const VLITracker = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [vliRecords, setVliRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddVLI, setShowAddVLI] = useState(false);
  const [editingVLI, setEditingVLI] = useState(null);
  const [vliProgress, setVliProgress] = useState({});
  const [analyzingPdf, setAnalyzingPdf] = useState(false);
  const [showUploadOption, setShowUploadOption] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false);
  
  // Get current month/year
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  
  // VLI form state
  const [vliForm, setVliForm] = useState({
    name: "",
    platform: "Insurance Dekho",
    month: currentMonth,
    year: currentYear,
    insurance_type: "Health",
    description: "",
    only_fresh: true,
    exclude_pa: true,
    exclude_port: true,
    exclude_renewal: true,
    exclude_zero_payout: true,
    first_year_only_multi_year: true,
    slabs: [
      { min_premium: 25000, payout_percent: 4 },
      { min_premium: 45000, payout_percent: 6 },
      { min_premium: 75000, payout_percent: 8 },
      { min_premium: 100000, payout_percent: 10 },
      { min_premium: 175000, payout_percent: 12 },
      { min_premium: 250000, payout_percent: 15 }
    ],
    additional_rules: ""
  });

  useEffect(() => {
    fetchVLIRecords();
  }, []);

  const fetchVLIRecords = async () => {
    try {
      setLoading(true);
      const res = await authGet('/api/advisor/vli');
      if (res.ok) {
        setVliRecords(res.data.vli_records || []);
        // Fetch progress for each VLI record
        for (const vli of res.data.vli_records || []) {
          fetchVLIProgress(vli.id);
        }
      }
    } catch (error) {
      console.error("Error fetching VLI records:", error);
      toast.error("Failed to load VLI records");
    } finally {
      setLoading(false);
    }
  };

  const fetchVLIProgress = async (vliId) => {
    try {
      const res = await authGet(`/api/advisor/vli/${vliId}/progress`);
      if (res.ok) {
        setVliProgress(prev => ({
          ...prev,
          [vliId]: res.data
        }));
      }
    } catch (error) {
      console.error("Error fetching VLI progress:", error);
    }
  };

  const handleSaveVLI = async () => {
    if (!vliForm.name) {
      toast.error("VLI name is required");
      return;
    }

    try {
      let res;
      if (editingVLI) {
        res = await authPut(`/api/advisor/vli/${editingVLI.id}`, vliForm);
      } else {
        res = await authPost('/api/advisor/vli', vliForm);
      }

      if (res.ok) {
        toast.success(editingVLI ? "VLI updated!" : "VLI created!");
        setShowAddVLI(false);
        setEditingVLI(null);
        resetForm();
        fetchVLIRecords();
      } else {
        toast.error(res.data?.detail || "Failed to save VLI");
      }
    } catch (error) {
      console.error("Error saving VLI:", error);
      toast.error("Failed to save VLI");
    }
  };

  const handleDeleteVLI = async (vliId) => {
    if (!window.confirm("Are you sure you want to delete this VLI record?")) return;
    
    try {
      const res = await authDelete(`/api/advisor/vli/${vliId}`);
      if (res.ok) {
        toast.success("VLI record deleted");
        fetchVLIRecords();
      } else {
        toast.error("Failed to delete VLI record");
      }
    } catch (error) {
      toast.error("Failed to delete VLI record");
    }
  };

  const resetForm = () => {
    setVliForm({
      name: "",
      platform: "Insurance Dekho",
      month: currentMonth,
      year: currentYear,
      insurance_type: "Health",
      description: "",
      only_fresh: true,
      exclude_pa: true,
      exclude_port: true,
      exclude_renewal: true,
      exclude_zero_payout: true,
      first_year_only_multi_year: true,
      slabs: [],
      additional_rules: ""
    });
    setShowUploadOption(true);
  };

  // Handle PDF upload and AI analysis
  const handlePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a PDF or image file");
      return;
    }

    setAnalyzingPdf(true);
    toast.info("Analyzing VLI document with AI...", { duration: 10000 });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('advisor_token');
      
      const response = await fetch(`${API_URL}/api/advisor/vli/analyze-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const extracted = data.vli_data;
        setVliForm({
          name: extracted.name || `VLI ${MONTHS[extracted.month || currentMonth]} ${extracted.year || currentYear}`,
          platform: extracted.platform || "Insurance Dekho",
          month: extracted.month ?? currentMonth,
          year: extracted.year || currentYear,
          insurance_type: extracted.insurance_type || "Health",
          description: extracted.description || "",
          only_fresh: extracted.only_fresh !== false,
          exclude_pa: extracted.exclude_pa !== false,
          exclude_port: extracted.exclude_port !== false,
          exclude_renewal: extracted.exclude_renewal !== false,
          exclude_zero_payout: extracted.exclude_zero_payout !== false,
          first_year_only_multi_year: extracted.first_year_only_multi_year !== false,
          slabs: extracted.slabs || [],
          additional_rules: extracted.additional_rules || ""
        });
        
        setShowUploadOption(false);
        toast.success("VLI details extracted successfully! Please review and save.");
      } else {
        toast.error(data.detail || "Failed to analyze document");
      }
    } catch (error) {
      console.error("Error analyzing PDF:", error);
      toast.error("Failed to analyze VLI document");
    } finally {
      setAnalyzingPdf(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const openEditModal = (vli) => {
    setEditingVLI(vli);
    setShowUploadOption(false);
    setVliForm({
      name: vli.name,
      platform: vli.platform || "Insurance Dekho",
      month: vli.month ?? currentMonth,
      year: vli.year || currentYear,
      insurance_type: vli.insurance_type || "Health",
      description: vli.description || "",
      only_fresh: vli.only_fresh !== false,
      exclude_pa: vli.exclude_pa !== false,
      exclude_port: vli.exclude_port !== false,
      exclude_renewal: vli.exclude_renewal !== false,
      exclude_zero_payout: vli.exclude_zero_payout !== false,
      first_year_only_multi_year: vli.first_year_only_multi_year !== false,
      slabs: vli.slabs || [],
      additional_rules: vli.additional_rules || ""
    });
    setShowAddVLI(true);
  };

  const addSlab = () => {
    setVliForm(prev => ({
      ...prev,
      slabs: [...prev.slabs, { min_premium: 0, payout_percent: 0 }]
    }));
  };

  const removeSlab = (index) => {
    setVliForm(prev => ({
      ...prev,
      slabs: prev.slabs.filter((_, i) => i !== index)
    }));
  };

  const updateSlab = (index, field, value) => {
    setVliForm(prev => ({
      ...prev,
      slabs: prev.slabs.map((slab, i) => 
        i === index ? { ...slab, [field]: parseFloat(value) || 0 } : slab
      )
    }));
  };

  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    } else if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(0)}K`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getCurrentSlab = (progress, slabs) => {
    if (!progress || !slabs) return null;
    const achieved = progress.net_premium || 0;
    let currentSlab = null;
    
    for (const slab of slabs) {
      if (achieved >= slab.min_premium) {
        currentSlab = slab;
      }
    }
    return currentSlab;
  };

  const getNextSlab = (progress, slabs) => {
    if (!progress || !slabs) return slabs?.[0] || null;
    const achieved = progress.net_premium || 0;
    
    for (const slab of slabs) {
      if (achieved < slab.min_premium) {
        return slab;
      }
    }
    return null;
  };

  // Get the top/highest slab for overall progress display
  const getTopSlab = (slabs) => {
    if (!slabs || slabs.length === 0) return null;
    return slabs.reduce((max, slab) => 
      slab.min_premium > max.min_premium ? slab : max
    , slabs[0]);
  };

  // Calculate progress percentage towards top slab
  const getTopSlabProgressPercentage = (netPremium, topSlab) => {
    if (!topSlab) return 0;
    return Math.min(100, Math.max(0, (netPremium / topSlab.min_premium) * 100));
  };

  const getVLIStatus = (vli) => {
    const now = new Date();
    const vliMonth = new Date(vli.year, vli.month, 1);
    const vliEnd = new Date(vli.year, vli.month + 1, 0);
    
    if (now < vliMonth) return { status: "upcoming", label: "Upcoming", color: "text-[#2E3B57]", bg: "bg-[#EDF1F8]/40" };
    if (now > vliEnd) return { status: "ended", label: "Completed", color: "text-[#3A4557]", bg: "bg-gray-500/20" };
    return { status: "active", label: "Active", color: "text-emerald-600", bg: "bg-emerald-500/20" };
  };

  return (
    <div className="min-h-screen bg-[#F4F6FA] pb-24 md:pb-0">
      {/* Mobile Sidebar */}
      <MobileSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />

      {/* Mobile Header */}
      <header className="md:hidden bg-white/95 backdrop-blur-xl sticky top-0 z-40 safe-area-inset-top border-b border-[#C9D4E6]">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowSidebar(true)}
              className="p-2 -ml-2 rounded-xl bg-[#EDF1F8] hover:bg-[#E2E9F4] active:bg-[#E2E9F4] transition-colors"
            >
              <Menu className="h-5 w-5 text-[#1B2A4A]" />
            </button>
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-[#1B2A4A]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#1B2A4A]">VLI Tracker</h1>
              <p className="text-xs text-[#3A4557]">Volume Incentive</p>
            </div>
          </div>
          
          <button
            onClick={() => { resetForm(); setEditingVLI(null); setShowAddVLI(true); }}
            className="p-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 active:scale-95 transition-transform"
          >
            <Plus className="h-5 w-5 text-[#1B2A4A]" />
          </button>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="hidden md:block bg-white/80 backdrop-blur-xl border-b border-[#C9D4E6] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/advisor/dashboard')}
                className="text-[#3A4557] hover:text-[#1B2A4A] transition-colors"
              >
                ← Back to Dashboard
              </button>
              <div className="h-6 w-px bg-[#E2E9F4]" />
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-[#1B2A4A]" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-[#1B2A4A]">VLI Tracker</h1>
                  <p className="text-xs text-[#3A4557]">Volume Link Incentive - Monthly Bonus</p>
                </div>
              </div>
            </div>
            
            <Button
              onClick={() => { resetForm(); setEditingVLI(null); setShowAddVLI(true); }}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
              data-testid="add-vli-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add VLI Month
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        ) : vliRecords.length === 0 ? (
          <div className="text-center py-16">
            <div className="h-24 w-24 rounded-full bg-[#EDF1F8] flex items-center justify-center mx-auto mb-6">
              <TrendingUp className="h-12 w-12 text-[#3A4557]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1B2A4A] mb-2">No VLI Records Yet</h2>
            <p className="text-[#3A4557] mb-6">Add your first VLI month to start tracking your volume incentives</p>
            <Button
              onClick={() => { resetForm(); setShowAddVLI(true); }}
              className="bg-gradient-to-r from-emerald-500 to-teal-600"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First VLI Month
            </Button>
          </div>
        ) : (
          <div className="grid gap-6">
            {vliRecords.map((vli) => {
              const status = getVLIStatus(vli);
              const progress = vliProgress[vli.id];
              const currentSlab = getCurrentSlab(progress, vli.slabs);
              const nextSlab = getNextSlab(progress, vli.slabs);
              const topSlab = getTopSlab(vli.slabs);
              const netPremium = progress?.net_premium || 0;
              const estimatedBonus = currentSlab ? (netPremium * currentSlab.payout_percent / 100) : 0;
              const topSlabProgressPct = getTopSlabProgressPercentage(netPremium, topSlab);
              
              return (
                <div
                  key={vli.id}
                  className="bg-white/50 rounded-2xl border border-[#C9D4E6] overflow-hidden hover:border-[#C9D4E6] transition-all"
                  data-testid={`vli-card-${vli.id}`}
                >
                  {/* VLI Header */}
                  <div className="p-6 border-b border-[#C9D4E6]">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center">
                          <Percent className="h-7 w-7 text-emerald-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-xl font-bold text-[#1B2A4A]">{vli.name}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${status.bg} ${status.color}`}>
                              {status.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-[#3A4557]">
                            <span className="flex items-center gap-1">
                              <Building2 className="h-4 w-4" />
                              {vli.platform}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {MONTHS[vli.month]} {vli.year}
                            </span>
                            <span className="text-emerald-600 font-medium">
                              {vli.insurance_type} Insurance
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditModal(vli)}
                          className="p-2 rounded-lg bg-[#EDF1F8] hover:bg-[#E2E9F4] text-[#3A4557] hover:text-[#1B2A4A] transition-colors"
                          title="Edit VLI"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteVLI(vli.id)}
                          className="p-2 rounded-lg bg-[#EDF1F8] hover:bg-red-500/20 text-[#3A4557] hover:text-red-600 transition-colors"
                          title="Delete VLI"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Progress Section */}
                  <div className="p-6">
                    {/* Current Achievement */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-[#EDF1F8]/50 rounded-xl p-4">
                        <p className="text-[#3A4557] text-sm mb-1">Net Premium</p>
                        <p className="text-2xl font-bold text-[#1B2A4A]">
                          {formatCurrency(netPremium)}
                        </p>
                        <p className="text-xs text-[#3A4557] mt-1">
                          {progress?.policy_count || 0} policies
                        </p>
                      </div>
                      
                      <div className="bg-[#EDF1F8]/50 rounded-xl p-4">
                        <p className="text-[#3A4557] text-sm mb-1">Current Slab</p>
                        {currentSlab ? (
                          <div className="flex items-center gap-2">
                            <p className="text-2xl font-bold text-emerald-600">{currentSlab.payout_percent}%</p>
                            <span className="text-xs text-[#3A4557]">extra payout</span>
                          </div>
                        ) : (
                          <p className="text-lg text-[#3A4557]">Not qualified yet</p>
                        )}
                      </div>
                      
                      <div className="bg-[#EDF1F8]/50 rounded-xl p-4">
                        <p className="text-[#3A4557] text-sm mb-1">Est. Bonus</p>
                        <p className="text-2xl font-bold text-[#A87608]">
                          {formatCurrency(estimatedBonus)}
                        </p>
                        <p className="text-xs text-[#3A4557] mt-1">
                          on {formatCurrency(netPremium)}
                        </p>
                      </div>
                      
                      <div className="bg-[#EDF1F8]/50 rounded-xl p-4">
                        <p className="text-[#3A4557] text-sm mb-1">Next Target</p>
                        {nextSlab ? (
                          <>
                            <p className="text-lg font-semibold text-[#2E3B57]">{formatCurrency(nextSlab.min_premium)}</p>
                            <p className="text-xs text-[#3A4557] mt-1">
                              for {nextSlab.payout_percent}% payout
                            </p>
                          </>
                        ) : (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-5 w-5 text-emerald-600" />
                            <p className="text-lg font-semibold text-emerald-600">Max achieved!</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar - Shows progress to TOP slab with milestone markers */}
                    {topSlab && (
                      <div className="mb-6">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-[#3A4557]">
                            Progress to Top Slab: {topSlab.payout_percent}% payout
                          </span>
                          <span className="text-[#1B2A4A] font-medium">
                            {formatCurrency(netPremium)} / {formatCurrency(topSlab.min_premium)}
                          </span>
                        </div>
                        
                        {/* Progress bar with milestone markers */}
                        <div className="relative">
                          {/* Background track */}
                          <div className="h-4 bg-[#EDF1F8] rounded-full overflow-hidden">
                            {/* Filled progress */}
                            <div 
                              className="h-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-full transition-all duration-500"
                              style={{ width: `${topSlabProgressPct}%` }}
                            />
                          </div>
                          
                          {/* Milestone markers on the progress bar */}
                          <div className="absolute top-0 left-0 w-full h-4 pointer-events-none">
                            {vli.slabs?.slice().sort((a, b) => a.min_premium - b.min_premium).map((slab, index) => {
                              const position = (slab.min_premium / topSlab.min_premium) * 100;
                              const achieved = netPremium >= slab.min_premium;
                              const isTopSlab = slab.min_premium === topSlab.min_premium;
                              
                              // Skip rendering marker at 100% (end of bar)
                              if (isTopSlab) return null;
                              
                              return (
                                <div
                                  key={index}
                                  className="absolute top-0 transform -translate-x-1/2 flex flex-col items-center"
                                  style={{ left: `${position}%` }}
                                >
                                  {/* Marker line */}
                                  <div className={`w-0.5 h-4 ${achieved ? 'bg-emerald-400' : 'bg-[#E2E9F4]'}`} />
                                  {/* Achievement flag */}
                                  <div 
                                    className={`mt-1 w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                                      achieved 
                                        ? 'bg-emerald-500 shadow-lg shadow-emerald-500/50' 
                                        : 'bg-[#E2E9F4] border border-[#C9D4E6]'
                                    }`}
                                  >
                                    {achieved ? (
                                      <CheckCircle className="h-3 w-3 text-[#1B2A4A]" />
                                    ) : (
                                      <Target className="h-3 w-3 text-[#3A4557]" />
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          
                          {/* Top slab flag at end */}
                          <div 
                            className="absolute top-0 right-0 transform translate-x-1/2 flex flex-col items-center"
                          >
                            <div className={`w-0.5 h-4 ${topSlabProgressPct >= 100 ? 'bg-cyan-400' : 'bg-[#E2E9F4]'}`} />
                            <div 
                              className={`mt-1 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                                topSlabProgressPct >= 100 
                                  ? 'bg-gradient-to-r from-emerald-500 to-cyan-500 shadow-lg shadow-cyan-500/50' 
                                  : 'bg-[#E2E9F4] border-2 border-cyan-500/50'
                              }`}
                            >
                              <Award className={`h-3 w-3 ${topSlabProgressPct >= 100 ? 'text-[#1B2A4A]' : 'text-cyan-500'}`} />
                            </div>
                          </div>
                        </div>
                        
                        {/* Slab labels below the bar */}
                        <div className="relative mt-8 h-8">
                          {vli.slabs?.slice().sort((a, b) => a.min_premium - b.min_premium).map((slab, index) => {
                            const position = (slab.min_premium / topSlab.min_premium) * 100;
                            const achieved = netPremium >= slab.min_premium;
                            const isTopSlab = slab.min_premium === topSlab.min_premium;
                            
                            return (
                              <div
                                key={index}
                                className={`absolute transform -translate-x-1/2 text-center ${isTopSlab ? 'right-0 translate-x-1/2' : ''}`}
                                style={!isTopSlab ? { left: `${position}%` } : {}}
                              >
                                <p className={`text-xs font-medium whitespace-nowrap ${
                                  achieved ? 'text-emerald-600' : 'text-[#3A4557]'
                                }`}>
                                  {formatCurrency(slab.min_premium)}
                                </p>
                                <p className={`text-[10px] whitespace-nowrap ${
                                  achieved ? 'text-emerald-700' : 'text-[#3A4557]'
                                }`}>
                                  {slab.payout_percent}%
                                </p>
                              </div>
                            );
                          })}
                        </div>
                        
                        <p className="text-right text-xs text-[#3A4557] mt-2">
                          {topSlabProgressPct.toFixed(1)}% towards top slab
                        </p>
                      </div>
                    )}

                    {/* All VLI Slabs */}
                    <div>
                      <h4 className="text-sm font-semibold text-[#3A4557] mb-3">VLI Payout Slabs</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        {vli.slabs?.map((slab, index) => {
                          const achieved = netPremium >= slab.min_premium;
                          
                          return (
                            <div
                              key={index}
                              className={`p-3 rounded-xl border transition-all ${
                                achieved 
                                  ? 'bg-emerald-500/10 border-emerald-500/30' 
                                  : 'bg-[#EDF1F8]/30 border-[#C9D4E6]'
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`text-xs font-medium ${achieved ? 'text-emerald-600' : 'text-[#3A4557]'}`}>
                                  {formatCurrency(slab.min_premium)}+
                                </span>
                                {achieved && <CheckCircle className="h-3 w-3 text-emerald-600 ml-auto" />}
                              </div>
                              <p className={`text-xl font-bold ${achieved ? 'text-emerald-700' : 'text-[#2E3B57]'}`}>
                                {slab.payout_percent}%
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Rules Summary */}
                    <div className="mt-6 p-4 bg-[#EDF1F8]/30 rounded-xl">
                      <h4 className="text-sm font-semibold text-[#3A4557] mb-2">Applied Rules</h4>
                      <div className="flex flex-wrap gap-3 text-xs">
                        {vli.only_fresh && <span className="px-2 py-1 bg-emerald-500/20 text-emerald-600 rounded">Fresh Only</span>}
                        {vli.exclude_pa && <span className="px-2 py-1 bg-red-500/20 text-red-600 rounded">No PA</span>}
                        {vli.exclude_port && <span className="px-2 py-1 bg-red-500/20 text-red-600 rounded">No Port</span>}
                        {vli.exclude_renewal && <span className="px-2 py-1 bg-red-500/20 text-red-600 rounded">No Renewal</span>}
                        {vli.first_year_only_multi_year && <span className="px-2 py-1 bg-[#EDF1F8]/40 text-[#2E3B57] rounded">1st Year Only (Multi-Year)</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Add/Edit VLI Modal */}
      <Dialog open={showAddVLI} onOpenChange={setShowAddVLI}>
        <DialogContent className="bg-white border-[#C9D4E6] text-[#1B2A4A] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-500" />
              {editingVLI ? "Edit VLI" : "Add New VLI Month"}
            </DialogTitle>
            <DialogDescription className="text-[#3A4557]">
              {showUploadOption && !editingVLI 
                ? "Upload a VLI document and let AI extract the details"
                : "Review and configure VLI slabs for the month"}
            </DialogDescription>
          </DialogHeader>

          {/* PDF Upload Section */}
          {showUploadOption && !editingVLI && (
            <div className="py-6">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handlePdfUpload}
                className="hidden"
                id="vli-pdf-upload"
              />
              
              <div 
                onClick={() => !analyzingPdf && fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
                  ${analyzingPdf 
                    ? 'border-emerald-500/50 bg-emerald-500/10' 
                    : 'border-[#C9D4E6] hover:border-emerald-500/50 hover:bg-[#EDF1F8]/50'}`}
              >
                {analyzingPdf ? (
                  <div className="flex flex-col items-center gap-4">
                    <div className="h-16 w-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-[#1B2A4A] mb-1">Analyzing VLI Document...</p>
                      <p className="text-sm text-[#3A4557]">AI is extracting payout slabs and rules</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-4">
                    <div className="h-16 w-16 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center">
                      <Sparkles className="h-8 w-8 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-[#1B2A4A] mb-1">Upload VLI PDF or Image</p>
                      <p className="text-sm text-[#3A4557] mb-3">
                        AI will extract payout percentages, premium slabs, and eligibility rules
                      </p>
                      <div className="flex items-center justify-center gap-2 text-emerald-600 text-sm">
                        <FileUp className="h-4 w-4" />
                        Click to upload (PDF, PNG, JPG)
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-center gap-4">
                <div className="flex-1 h-px bg-[#E2E9F4]" />
                <span className="text-[#3A4557] text-sm">OR</span>
                <div className="flex-1 h-px bg-[#E2E9F4]" />
              </div>

              <button
                onClick={() => setShowUploadOption(false)}
                className="mt-4 w-full py-3 rounded-xl border border-[#C9D4E6] text-[#3A4557] hover:text-[#1B2A4A] hover:bg-[#EDF1F8] transition-all flex items-center justify-center gap-2"
              >
                <Edit2 className="h-4 w-4" />
                Enter Details Manually
              </button>
            </div>
          )}

          {/* Form Section */}
          {(!showUploadOption || editingVLI) && (
          <div className="space-y-6 py-4">
            {/* Success badge if AI extracted */}
            {!editingVLI && vliForm.slabs.length > 0 && (
              <div className="flex items-center gap-2 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                <Sparkles className="h-5 w-5 text-emerald-600" />
                <span className="text-emerald-600 text-sm font-medium">AI extracted VLI details - Please review and save</span>
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#3A4557] text-sm">VLI Name *</Label>
                <Input
                  value={vliForm.name}
                  onChange={(e) => setVliForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Health Payout Incentive Feb 2026"
                  className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                  data-testid="vli-name-input"
                />
              </div>
              <div>
                <Label className="text-[#3A4557] text-sm">Platform</Label>
                <Select 
                  value={vliForm.platform} 
                  onValueChange={(value) => setVliForm(prev => ({ ...prev, platform: value }))}
                >
                  <SelectTrigger className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#C9D4E6]">
                    <SelectItem value="Insurance Dekho">Insurance Dekho</SelectItem>
                    <SelectItem value="PolicyBazaar">PolicyBazaar</SelectItem>
                    <SelectItem value="Turtlemint">Turtlemint</SelectItem>
                    <SelectItem value="RenewBuy">RenewBuy</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#3A4557] text-sm">Month</Label>
                <Select 
                  value={String(vliForm.month)} 
                  onValueChange={(value) => setVliForm(prev => ({ ...prev, month: parseInt(value) }))}
                >
                  <SelectTrigger className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#C9D4E6]">
                    {MONTHS.map((month, index) => (
                      <SelectItem key={index} value={String(index)}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#3A4557] text-sm">Year</Label>
                <Input
                  type="number"
                  value={vliForm.year}
                  onChange={(e) => setVliForm(prev => ({ ...prev, year: parseInt(e.target.value) || currentYear }))}
                  className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                  min="2020"
                  max="2030"
                />
              </div>
              <div>
                <Label className="text-[#3A4557] text-sm">Insurance Type</Label>
                <Select 
                  value={vliForm.insurance_type} 
                  onValueChange={(value) => setVliForm(prev => ({ ...prev, insurance_type: value }))}
                >
                  <SelectTrigger className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#C9D4E6]">
                    <SelectItem value="Health">Health Insurance</SelectItem>
                    <SelectItem value="Life">Life Insurance</SelectItem>
                    <SelectItem value="Motor">Motor Insurance</SelectItem>
                    <SelectItem value="All">All Types</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Eligibility Rules */}
            <div className="p-4 bg-[#EDF1F8]/30 rounded-xl">
              <h4 className="font-semibold text-[#1B2A4A] mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-[#A87608]" />
                Eligibility Rules
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <label className="flex items-center gap-2 text-sm text-[#3A4557] p-2 bg-white/50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={vliForm.only_fresh}
                    onChange={(e) => setVliForm(prev => ({ ...prev, only_fresh: e.target.checked }))}
                    className="rounded"
                  />
                  Fresh/New Policies Only
                </label>
                <label className="flex items-center gap-2 text-sm text-[#3A4557] p-2 bg-white/50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={vliForm.exclude_pa}
                    onChange={(e) => setVliForm(prev => ({ ...prev, exclude_pa: e.target.checked }))}
                    className="rounded"
                  />
                  Exclude PA Cases
                </label>
                <label className="flex items-center gap-2 text-sm text-[#3A4557] p-2 bg-white/50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={vliForm.exclude_port}
                    onChange={(e) => setVliForm(prev => ({ ...prev, exclude_port: e.target.checked }))}
                    className="rounded"
                  />
                  Exclude Port/Portability
                </label>
                <label className="flex items-center gap-2 text-sm text-[#3A4557] p-2 bg-white/50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={vliForm.exclude_renewal}
                    onChange={(e) => setVliForm(prev => ({ ...prev, exclude_renewal: e.target.checked }))}
                    className="rounded"
                  />
                  Exclude Renewals
                </label>
                <label className="flex items-center gap-2 text-sm text-[#3A4557] p-2 bg-white/50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={vliForm.exclude_zero_payout}
                    onChange={(e) => setVliForm(prev => ({ ...prev, exclude_zero_payout: e.target.checked }))}
                    className="rounded"
                  />
                  Exclude Zero Payout
                </label>
                <label className="flex items-center gap-2 text-sm text-[#3A4557] p-2 bg-white/50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={vliForm.first_year_only_multi_year}
                    onChange={(e) => setVliForm(prev => ({ ...prev, first_year_only_multi_year: e.target.checked }))}
                    className="rounded"
                  />
                  1st Year Only (Multi-Year)
                </label>
              </div>
            </div>

            {/* Payout Slabs */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-[#1B2A4A] flex items-center gap-2">
                  <Percent className="h-4 w-4 text-emerald-500" />
                  Payout Slabs
                </h4>
                <Button
                  onClick={addSlab}
                  size="sm"
                  variant="outline"
                  className="border-[#C9D4E6] text-[#3A4557] hover:text-[#1B2A4A]"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Slab
                </Button>
              </div>
              
              <div className="space-y-3">
                {vliForm.slabs.map((slab, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-[#EDF1F8]/30 rounded-lg">
                    <div className="flex-1">
                      <Label className="text-[#3A4557] text-xs">Min Premium (₹)</Label>
                      <Input
                        type="number"
                        value={slab.min_premium}
                        onChange={(e) => updateSlab(index, 'min_premium', e.target.value)}
                        placeholder="e.g., 25000"
                        className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                      />
                    </div>
                    <div className="w-32">
                      <Label className="text-[#3A4557] text-xs">Payout %</Label>
                      <Input
                        type="number"
                        value={slab.payout_percent}
                        onChange={(e) => updateSlab(index, 'payout_percent', e.target.value)}
                        placeholder="e.g., 4"
                        className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                        step="0.5"
                      />
                    </div>
                    <button
                      onClick={() => removeSlab(index)}
                      className="p-2 mt-5 text-[#3A4557] hover:text-red-600 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {vliForm.slabs.length === 0 && (
                  <p className="text-center text-[#3A4557] py-4">No slabs added. Click "Add Slab" to create payout tiers.</p>
                )}
              </div>
            </div>

            {/* Additional Notes */}
            <div>
              <Label className="text-[#3A4557] text-sm">Additional Notes / Rules</Label>
              <textarea
                value={vliForm.additional_rules}
                onChange={(e) => setVliForm(prev => ({ ...prev, additional_rules: e.target.value }))}
                placeholder="Any additional terms, FLP conditions, etc..."
                className="mt-1 w-full h-20 px-3 py-2 bg-white/70 border border-[#C9D4E6] rounded-lg text-[#1B2A4A] resize-none"
              />
            </div>
          </div>
          )}

          {/* Footer buttons */}
          {(!showUploadOption || editingVLI) && (
          <div className="flex justify-end gap-3 pt-4 border-t border-[#C9D4E6]">
            <Button
              variant="outline"
              onClick={() => { setShowAddVLI(false); setEditingVLI(null); resetForm(); }}
              className="border-[#C9D4E6] text-[#3A4557]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveVLI}
              className="bg-gradient-to-r from-emerald-500 to-teal-600"
              data-testid="save-vli-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {editingVLI ? "Update VLI" : "Create VLI"}
            </Button>
          </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav onMenuClick={() => setShowSidebar(true)} />
    </div>
  );
};

export default VLITracker;
